import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Badge, Button, cn, Dialog, Input, Switch } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColourClient, type ModelCellDTO, type ModelColumnDTO, type ModelFamilyDTO, type NeedDTO } from "../../api/configurator";
import { OpenRecord } from "../../components/OpenRecord";
import { NexpimView } from "../../views";
import { COLOURS_KEY } from "./PartColoursTab";
import { ProductSyncDialog } from "./ProductSyncDialog";

/**
 * The model grid (design §4): every product family (its variation group) as
 * sizes against colours, each size a model. A cell is the product, a gap is a
 * colour the family is sold in that this size has no product for. A gap opens
 * what making it would need (§6): each coloured piece's sister in that colour,
 * there or to make, and the panels and edgebands to make it from.
 */
export function ProductModelsPage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const client = useMemo(() => new ColourClient(http), [http]);
  const allowed = hasView(NexpimView.MANUFACTURING_PARTS);
  const [gapsOnly, setGapsOnly] = useState(true);
  const [filter, setFilter] = useState("");
  const [gap, setGap] = useState<{ family: ModelFamilyDTO; size: string; column: ModelColumnDTO } | null>(null);
  const grid = useQuery({ queryKey: [...COLOURS_KEY, "models"], queryFn: () => client.models(), enabled: allowed });
  const title = t("navigation.item.engineering-models", "Product models");

  const families = (grid.data?.families ?? []).filter((family) => {
    if (gapsOnly && family.gaps === 0) return false;
    const term = filter.trim().toLowerCase();
    return term === "" || family.name.toLowerCase().includes(term) || family.cells.some((cell) => cell.sku.toLowerCase().startsWith(term));
  });

  if (!allowed) {
    return (
      <>
        <PageHeader title={title} />
        <p className="p-4 text-[13px] text-ink-muted lg:p-6">{t("error.access-denied.detail", "Your account does not have permission to view this page.")}</p>
      </>
    );
  }
  return (
    <>
      <PageHeader
        title={title}
        subtitle={t(
          "model.subtitle",
          "Each product family as its sizes against its colours: one model per size. A cell is a product that exists, whatever its status; a gap is a colour the family has with no product in this size: open it to see what making it takes.",
        )}
      />
      <div className="flex shrink-0 flex-wrap items-center gap-4 border-b border-line px-4 py-2 lg:px-6">
        <Input className="max-w-64" placeholder={t("model.filter", "Name or SKU")} value={filter} onChange={(event) => setFilter(event.target.value)} />
        <Switch checked={gapsOnly} onChange={setGapsOnly} label={t("model.gaps-only", "Families with gaps only")} />
        {grid.data && (
          <span className="text-[13px] text-ink-muted">
            {t("model.counts", "{{families}} families · {{models}} models · {{cells}} products · {{gaps}} gaps", {
              families: grid.data.families.length,
              models: grid.data.models,
              cells: grid.data.cells,
              gaps: grid.data.gaps,
            })}
          </span>
        )}
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4 lg:p-6">
        {grid.isLoading && <p className="text-[13px] text-ink-muted">{t("table.loading", "Loading")}</p>}
        {families.map((family) => (
          <FamilyGrid key={family.variation_group} family={family} onGap={(size, column) => setGap({ family, size, column })} />
        ))}
        {grid.data && families.length === 0 && <p className="text-[13px] text-ink-muted">{t("model.none", "No family matches.")}</p>}
        {grid.data && grid.data.unplaced.length > 0 && (
          <section className="rounded-md border border-line p-3">
            <h3 className="mb-1 text-[13px] font-semibold">{t("model.unplaced", "In a family but with no COLOR: not on the grid")}</h3>
            <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
              {grid.data.unplaced.map((one) => (
                <li key={one.product_id}>
                  <Link to="/products/$productId" params={{ productId: String(one.product_id) }} className="font-mono text-accent hover:underline">{one.sku}</Link>
                  <span className="ml-1 text-ink-muted">{one.name}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
      {gap && <GapDialog family={gap.family} size={gap.size} column={gap.column} onClose={() => setGap(null)} />}
    </>
  );
}

function FamilyGrid({ family, onGap }: { family: ModelFamilyDTO; onGap: (size: string, column: ModelColumnDTO) => void }) {
  const { t } = useTranslation();
  const cell = (size: string, column: ModelColumnDTO): ModelCellDTO | undefined =>
    family.cells.find((one) => one.size === size && one.colour.toUpperCase() === column.sales_name.toUpperCase());
  return (
    <section className="rounded-md border border-line">
      <header className="flex flex-wrap items-baseline gap-2 border-b border-line px-3 py-2">
        <h3 className="text-[14px] font-semibold">{family.name}</h3>
        <span className="font-mono text-[12px] text-ink-muted">{family.base}</span>
        {family.gaps > 0 && <Badge size="sm" variant="warning">{t("model.gap-count", "{{n}} gaps", { n: family.gaps })}</Badge>}
      </header>
      <div className="overflow-auto">
        <table className="w-full border-separate border-spacing-0 text-[12px]">
          <thead>
            <tr>
              <th className="px-3 py-1 text-left font-medium text-ink-muted">{t("model.size", "Size")}</th>
              {family.colours.map((column) => (
                <th key={column.sales_name} className="whitespace-nowrap px-3 py-1 text-left font-medium">
                  {column.sales_name}
                  <span className="ml-1 font-normal text-ink-muted">
                    {column.primary_code ? column.primary_code + (column.accent_code ? ` + ${column.accent_code}` : "") : t("model.no-scheme", "no part colours")}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {family.sizes.map((size) => (
              <tr key={size}>
                <td className="whitespace-nowrap border-t border-line px-3 py-1 font-medium">{size === "" ? t("model.one-size", "One size") : size}</td>
                {family.colours.map((column) => {
                  const found = cell(size, column);
                  return (
                    <td key={column.sales_name} className="whitespace-nowrap border-t border-line px-3 py-1">
                      {found ? (
                        <span className="inline-flex items-center gap-1">
                          <Link to="/products/$productId" params={{ productId: String(found.product_id) }}
                            className={cn("font-mono hover:underline", found.status === "AVAILABLE" ? "text-accent" : "text-ink-muted")}>
                            {found.sku}
                          </Link>
                          {found.status !== "AVAILABLE" && <Badge size="sm" variant="neutral">{found.status.toLowerCase().replaceAll("_", " ")}</Badge>}
                        </span>
                      ) : (
                        <button type="button" disabled={!column.primary_colour_id}
                          title={column.primary_colour_id ? undefined : t("model.gap.no-scheme", "Say which part colours this product colour is made in first (Colours, Product colours).")}
                          onClick={() => onGap(size, column)}
                          className="rounded border border-dashed border-warn px-2 py-0.5 text-warn hover:bg-warn-soft disabled:border-line disabled:text-ink-muted">
                          {t("model.gap", "missing")}
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function needLabel(need: NeedDTO, t: (key: string, fallback: string, options?: Record<string, unknown>) => string): { text: string; tone: "success" | "warning" | "error" | "neutral" } {
  switch (need.state) {
    case "SAME":
      return { text: t("model.need.same", "Same colour: kept"), tone: "neutral" };
    case "EXISTS":
      return { text: t("model.need.exists", "Exists: {{part}}", { part: need.sister_number }), tone: "success" };
    case "INACTIVE":
      return { text: t("model.need.inactive", "Only an inactive one: {{part}}", { part: need.sister_number }), tone: "warning" };
    case "MISSING":
      return need.role === "PIECE"
        ? { text: t("model.need.make", "To make"), tone: "warning" }
        : { text: t("model.need.missing", "None in {{colour}}", { colour: need.target_code }), tone: "error" };
    default:
      return { text: t("model.need.not-linked", "Not linked: link its sisters"), tone: "error" };
  }
}

/** A gap: what the size's product in another colour would need in this one. */
function GapDialog({ family, size, column, onClose }: { family: ModelFamilyDTO; size: string; column: ModelColumnDTO; onClose: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new ColourClient(http), [http]);
  const references = family.cells.filter((one) => one.size === size);
  const [referenceId, setReferenceId] = useState(references[0]?.product_id ?? 0);
  const [syncing, setSyncing] = useState(false);
  const reference = references.find((one) => one.product_id === referenceId);
  const feasibility = useQuery({
    queryKey: [...COLOURS_KEY, "feasibility", referenceId, column.primary_colour_id, column.accent_colour_id],
    queryFn: () => client.feasibility(referenceId, column.primary_colour_id!, column.accent_colour_id),
    enabled: referenceId !== 0 && column.primary_colour_id != null,
  });
  const data = feasibility.data;
  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={t("model.gap.title", "{{family}} {{size}} in {{colour}}", { family: family.name, size, colour: column.sales_name })}
      description={t("model.gap.from", "Taken from another colour of the same size: its coloured pieces, each in {{colour}}.", { colour: column.primary_code })}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>{t("dialog.close", "Close")}</Button>
          {reference && column.scheme_id != null && (
            <Button variant="primary" onClick={() => setSyncing(true)}>
              {t("model.gap.sync", "Create from {{sku}}", { sku: reference.sku })}
            </Button>
          )}
        </>
      }
    >
      {syncing && column.scheme_id != null && (
        <ProductSyncDialog productId={referenceId} preselect={{ scheme_id: column.scheme_id }} onClose={() => setSyncing(false)} />
      )}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-[13px]">
        <span className="text-ink-muted">{t("model.gap.reference", "From")}</span>
        {references.map((one) => (
          <Button key={one.product_id} size="sm" variant={one.product_id === referenceId ? "primary" : "secondary"} onClick={() => setReferenceId(one.product_id)}>
            {one.sku}
          </Button>
        ))}
      </div>
      {feasibility.isLoading && <p className="text-[13px] text-ink-muted">{t("table.loading", "Loading")}</p>}
      {feasibility.isError && <p className="text-[13px] text-risk">{t("error.generic", "Something went wrong.")}</p>}
      {data && (
        <>
          <p className="mb-2 text-[13px]">
            {data.blocking > 0
              ? t("model.gap.blocked", "{{n}} materials have no {{colour}} sister: link or add them before this can be made.", { n: data.blocking, colour: column.primary_code })
              : t("model.gap.ready", "Every material exists in {{colour}}: {{pieces}} pieces to make from them.", { colour: column.primary_code, pieces: data.pieces_to_make })}
          </p>
          <ul className="max-h-[60vh] divide-y divide-line overflow-auto rounded-md border border-line text-[13px]">
            {data.needs.map((need) => {
              const label = needLabel(need, t);
              return (
                <li key={`${need.role}:${String(need.part_id)}`} className={cn("flex flex-wrap items-center gap-2 px-2 py-1", need.role !== "PIECE" && "pl-8")}>
                  <Badge size="sm" variant="neutral">{need.role === "PIECE" ? t("colour.kind.piece", "Cut pieces") : need.role === "PANEL" ? t("colour.kind.panel", "Panels") : t("colour.kind.edgeband", "Edgebands")}</Badge>
                  <OpenRecord target={{ kind: "part", id: need.part_id, tab: "colours" }} label={need.part_number} />
                  <span className="font-mono">{need.part_number}</span>
                  <span className="min-w-0 flex-1 truncate text-ink-muted">{need.part_name}</span>
                  <span className="text-ink-muted">{need.colour_code} → {need.target_code}</span>
                  <Badge size="sm" variant={label.tone}>{label.text}</Badge>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Dialog>
  );
}
